import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormatPricePipe } from '@epicuro-next/ataraxia/formatting';
import { IListAnalyticsExchange } from '@epicuro-next/data/reporting-api';
import { IUser, UserRole } from '@epicuro-next/data/users';
import {
  selectCanSeeAnalyticsRoles,
  selectHotListFeatureDataForEntityFactory,
  selectListExchange,
} from '@epicuro-next/state/analytics-entities';
import { selectListEntityFactory } from '@epicuro-next/state/list-entities';
import { selectUser } from '@epicuro-next/state/user-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { combineLatest, from, Observable } from 'rxjs';
import { filter, first, map, mergeMap, shareReplay } from 'rxjs/operators';

type hotListFeatureSelector = ReturnType<
  typeof selectHotListFeatureDataForEntityFactory
>;
type hotListFeatureSelectorResult = ReturnType<hotListFeatureSelector>;

@Component({
  selector: 'epc-hot-list-indicator',
  templateUrl: './hot-list-indicator.component.html',
  styleUrls: ['./hot-list-indicator.component.scss'],
  providers: [FormatPricePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HotListIndicatorComponent implements OnChanges {
  @Input()
  public listId!: string;

  @Input()
  public listIds!: string[];

  @Input()
  public showAlmostHotIconBasedOnUserRole = false;

  public hotListInfo$!: Observable<hotListFeatureSelectorResult>;

  public toolTipText$!: Observable<string | undefined>;

  public exchangeRate$ = this.store.pipe(select(selectListExchange));

  public currentUser$ = this.store.pipe(
    select(selectUser),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public isListInMyLists$!: Observable<boolean>;

  private analyticsUserRoles$ = this.store.pipe(
    select(selectCanSeeAnalyticsRoles),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public showAlmostHotIcon$!: Observable<boolean>;

  constructor(private store: Store, private formatPricePipe: FormatPricePipe) {}

  public ngOnChanges(changes: SimpleChanges) {
    const listChange = changes.listId;

    if (listChange && listChange.previousValue !== listChange.currentValue) {
      this.setUpInfoStreams();
    }
  }

  public getUserInfo(listId: string) {
    return this.store.pipe(
      select(selectListEntityFactory(listId)),
      map(Boolean),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private setUpInfoStreams() {
    if (this.listIds && this.listIds.length) {
      this.isListInMyLists$ = this.getUserInfo(this.listIds[0]);

      this.hotListInfo$ = from(this.listIds).pipe(
        mergeMap((listId) =>
          this.store.pipe(
            select(selectHotListFeatureDataForEntityFactory(listId)),
          ),
        ),
        filter(isNonNullish),
        first(({ hot, almostHot }) => !!hot || !!almostHot),
      );
    } else {
      this.isListInMyLists$ = this.getUserInfo(this.listId);

      this.hotListInfo$ = this.store.pipe(
        select(selectHotListFeatureDataForEntityFactory(this.listId)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }

    this.showAlmostHotIcon();
    this.setUpToolTipText();
  }

  private setUpToolTipText() {
    this.toolTipText$ = combineLatest([
      this.hotListInfo$,
      this.exchangeRate$,
    ]).pipe(
      map(([info, exchangeRate]) => {
        if (!info) return undefined;
        if (info.hot) {
          return $localize`:Analytics|Hot list tooltip@@analyticsHotListToolTip:Trending: Highest revenue this month`;
        }
        if (info.almostHot) {
          const revenueStr = this.convertCurrency(info.almostHot, exchangeRate);
          return $localize`:Analytics|Almost trending tooltip@@analyticsAlmostTrendingTooltip:Only ${revenueStr} more to be the best this month!`;
        }
        return undefined;
      }),
    );
  }

  private convertCurrency(
    almostHot: number,
    exchangeRate?: IListAnalyticsExchange,
  ) {
    const revenue = parseFloat(
      (almostHot * (exchangeRate?.rate ?? 1)).toFixed(0),
    );
    const currency = exchangeRate?.currency ?? 'EUR';

    return `${this.formatPricePipe.transform(revenue, currency)}`;
  }

  private showAlmostHotIcon() {
    this.showAlmostHotIcon$ = combineLatest([
      this.currentUser$,
      this.analyticsUserRoles$,
      this.isListInMyLists$,
    ]).pipe(
      map(([currentUser, analyticsUserRoles, isListInMyLists]) =>
        this.shouldShowAlmostHotIcon(
          currentUser,
          analyticsUserRoles,
          isListInMyLists,
        ),
      ),
    );
  }

  private shouldShowAlmostHotIcon(
    currentUser: IUser | null,
    analyticsUserRoles: UserRole[],
    isListInMyLists: boolean,
  ) {
    const showAlmostHotListBasedOnRoles =
      this.showAlmostHotIconBasedOnUserRole &&
      this.checkUserRolesInAnalyticsAllowedRoles(
        currentUser?.roles,
        analyticsUserRoles,
      );

    const showAlmostHotListBasedOnUserAndRoles =
      !this.showAlmostHotIconBasedOnUserRole &&
      isListInMyLists &&
      this.checkUserRolesInAnalyticsAllowedRoles(
        currentUser?.roles,
        analyticsUserRoles,
      );

    return (
      showAlmostHotListBasedOnRoles || showAlmostHotListBasedOnUserAndRoles
    );
  }

  private checkUserRolesInAnalyticsAllowedRoles(
    roles: readonly UserRole[] | undefined,
    analyticsUserRoles: UserRole[],
  ) {
    return analyticsUserRoles.some((role) => roles?.includes(role));
  }
}
